import React, { useMemo } from 'react';
import {
  AsyncConnector,
  BadRequestError,
  Button,
  CondensedHeader,
  CondensedLarge,
  Dropdown,
  Form,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  ToastUtil,
} from '@axiom/ui';
import type { Account, Contact } from '@axiom/validation';
import { AhqFindContactSchema } from '@axiom/validation';

import { AccountsApi } from '../../api/accounts-api';
import { AccountsLegacyApi } from '../../api/accounts-legacy-api';
import { ContactsApi } from '../../api/contacts-api';

type ReadContactsResponseType = {
  data: Contact[];
  meta: {
    currentPage: number;
    pageCount: number;
    resultCount: number;
  };
};

export type FindContactForAccountModalProps = {
  onClose: () => void;
  onSuccess?: () => void;
  accountId: Account['id'];
};

export const FindContactForAccountModal = ({
  onClose,
  onSuccess,
  accountId,
}: FindContactForAccountModalProps) => {
  const contactsConnector = useMemo(
    () =>
      new AsyncConnector<ReadContactsResponseType>({
        getData: args => {
          return ContactsApi.readContacts({
            search: args.search,
            page: args.page,
          });
        },
        parseData: results => {
          const contacts = results.data.map(contact => ({
            id: contact.id,
            name: `${contact.firstName} ${contact.lastName}`,
          }));

          return {
            currentPage: results.meta.currentPage,
            totalPages: results.meta.pageCount,
            data: contacts,
          };
        },
      }),
    []
  );

  return (
    <Form
      name="FIND_CONTACT_FOR_ACCOUNT_FORM"
      schema={AhqFindContactSchema}
      initialValues={{
        contactId: undefined,
      }}
      onSubmit={async formData => {
        const { contactId } = formData;
        if (contactId) {
          try {
            await AccountsLegacyApi.createAccountContacts({
              accountId,
              contactIds: [contactId],
            });
            await AccountsApi.invalidateAccount(accountId);
          } catch (error) {
            if (!(error instanceof BadRequestError)) throw error;
            const message =
              (error.getErrors() as { message?: string } | undefined)
                ?.message ?? 'An unknown issue occurred';
            ToastUtil.add({
              dismissible: true,
              name: 'FIND_CONTACT_FOR_ACCOUNT_ERROR',
              type: 'error',
              children: (
                <>
                  <CondensedHeader>Error</CondensedHeader>
                  <CondensedLarge>{message}</CondensedLarge>
                </>
              ),
            });
            return;
          }
          ToastUtil.add({
            dismissible: true,
            name: 'FIND_CONTACT_FOR_ACCOUNT_SUCCESS',
            type: 'info',
            children: <CondensedHeader>Contact added.</CondensedHeader>,
          });
          onSuccess?.();
          onClose();
        }
      }}
    >
      {({ fireSubmit }) => (
        <Modal name="FIND_CONTACT_FOR_ACCOUNT_MODAL">
          <ModalHeader
            name="FIND_CONTACT_FOR_ACCOUNT_MODAL_HEADER"
            onClose={() => {
              onClose();
            }}
          >
            Add Associated User
          </ModalHeader>
          <ModalSection
            name="FIND_CONTACT_FOR_ACCOUNT_MODAL_SECTION"
            disableScrolling
          >
            <Dropdown
              name="contactId"
              options={contactsConnector}
              displayKey="name"
              valueKey="id"
              placeholder="Type to start searching..."
            />
          </ModalSection>
          <ModalFooter name="FIND_CONTACT_FOR_ACCOUNT_MODAL_FOOTER">
            <Button
              onClick={() => {
                onClose();
              }}
              name="CANCEL"
              variation="outline"
            >
              Cancel
            </Button>
            <Button type="submit" onClick={fireSubmit} name="ADD">
              Add
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
